import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Card from './Card';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import Button from 'components/Button/Button';
import { createFragmentContainer, graphql } from 'react-relay';
import { ProfileCard_delegate } from './__generated__/ProfileCard_delegate.graphql';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBadgeCheck,
  faIdBadge,
  faRotateRight,
  faShieldCheck as duoShield,
  faSpinner,
  faUserCheck,
  faClipboardCheck
} from '@fortawesome/pro-duotone-svg-icons';
import { useDelegateDetailsDispatch } from 'views/DelegateDetailsProvider';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import RequestBackgroundCheckModal from 'features/background-checks/components/RequestBackgroundCheckModal/RequestBackgroundCheckModal';
import { ProfileCard_manager } from './__generated__/ProfileCard_manager.graphql';
import CheckStatus from 'features/background-checks/components/CheckStatus';
import {
  getStatus,
  statusText
} from 'features/background-checks/utils/helpers';
import DataItem from './DataItem';
import moment, { Moment } from 'moment';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import {
  faShieldCheck,
  faShieldExclamation
} from '@fortawesome/pro-solid-svg-icons';
import AutomatedCheckModal from 'features/background-checks/components/AutomatedCheckModal/AutomatedCheckModal';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
    maxWidth: '30rem'
  },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: '3rem',
    justifyContent: 'space-between'
  },
  profileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem'
  },
  profileText: {
    maxWidth: '14rem'
  },
  personalInfo: {},
  infoItems: {
    display: 'grid',
    gap: '2rem',
    gridTemplateColumns: '1fr 1fr'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  infoItem__label: {
    color: '#6A728F',
    margin: 0,
    padding: 0,
    fontSize: '0.8rem'
  },
  infoItem__text: {
    margin: 0,
    padding: 0,
    fontSize: '0.9rem'
  },
  subtitle: {
    fontSize: '0.9rem',
    fontWeight: 600
  },
  profileText__name: {
    fontSize: '1.2rem',
    margin: 0,
    fontWeight: 700
  },
  profileText_job: {
    fontSize: '0.8rem',
    fontWeight: 700,
    margin: 0,
    color: '#6A728F'
  },
  noBackgroundCheck: {
    color: '#6A728F',
    fontSize: '0.8rem'
  },
  backgroundCheckPending: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '2rem'
  },
  pending: {
    color: '#69728f',
    fontSize: '0.8rem'
  },
  bgItems: {
    display: 'flex',
    gap: '10px',
    marginTop: 10,
    flexWrap: 'wrap'
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    marginTop: 10
  }
}));

type Props = {
  delegate: ProfileCard_delegate;
  manager: ProfileCard_manager;
  onRequestBackgroundCheck(
    includesDBS: boolean,
    isRenewal: boolean,
    idExpiry?: Moment
  ): void;
};

function ProfileCard({ delegate, manager, onRequestBackgroundCheck }: Props) {
  const classes = useStyles();

  const dispatchEdit = useDelegateDetailsDispatch();
  const action = useActionMenuDispatch();

  const openDelegateModal = () => {
    dispatchEdit({
      type: 'show',
      uuid: delegate.uuid,
      onClose: () => window.location.reload()
    });
  };

  const onRequest = (evt: any) => {
    action({
      type: 'show',
      children: (
        <RequestBackgroundCheckModal
          renewalCost={manager.company.renewalPennyPrice}
          managerName={`${manager.firstName} ${manager.lastName}`}
          companyName={manager.company.name}
          hasInitial={delegate.hasActiveBackgroundCheck}
          userName={`${delegate.firstName} ${delegate.lastName}`}
          onRequest={(checked, isRenewal, idDate?: Moment) => {
            onRequestBackgroundCheck(checked, isRenewal, idDate);
            action({ type: 'hide' });
          }}
          onClose={() => action({ type: 'hide' })}
          checkCost={manager.company.backgroundCheckPennyPrice}
          dbsCost={manager.company.dbsPennyPrice}
          checksLeft={
            (manager.company?.invoicing?.backgroundChecksPrePurchased ?? 0) -
            (manager.company.invoicing?.backgroundChecksUsed ?? 0)
          }
          dbsLeft={
            (manager.company.invoicing?.dbsPrePurchased ?? 0) -
            (manager.company.invoicing?.dbsUsed ?? 0)
          }
          renewalsLeft={
            (manager.company.invoicing?.renewalsPrePurchased ?? 0) -
            (manager.company.invoicing?.renewalsUsed ?? 0)
          }
        />
      ),
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'modal'
      }
    });
  };

  const onUploadExisting = (evt: any) => {
    action({
      type: 'show',
      children: (
        <AutomatedCheckModal
          userName="test"
          delegateUUID={delegate.uuid}
          onClose={() => {
            action({ type: 'hide' });
            window.location.reload();
          }}
          successPennyPrice={manager.company.verificationSuccessPennyPrice}
          failurePennyPrice={manager.company.verificationFailurePennyPrice}
        />
      ),
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'modal'
      }
    });
  };

  return (
    <Card className={classes.container}>
      <div className={classes.profile}>
        <div className={classes.profileInfo}>
          <ProfileIcon
            name={`${delegate.firstName} ${delegate.lastName}`}
            size={80}
            fontSize={26}
            url={delegate.profileImageUrl ?? undefined}
          />
          <div className={classes.profileText}>
            <p
              className={classes.profileText__name}
            >{`${delegate.firstName} ${delegate.lastName}`}</p>
            <p className={classes.profileText_job}>
              {delegate.jobTitle ?? '-'}
            </p>
          </div>
        </div>
        <Button archetype="flat" onClick={openDelegateModal}>
          Edit details
        </Button>
      </div>
      <div className={classes.personalInfo}>
        <p className={classes.subtitle}>Personal Information</p>
        <div className={classes.infoItems}>
          <div className={classes.infoItem}>
            <p className={classes.infoItem__label}>TTC login</p>
            <p className={classes.infoItem__text}>{delegate.TTC_ID}</p>
          </div>
          <div className={classes.infoItem}>
            <p className={classes.infoItem__label}>Email</p>
            <p className={classes.infoItem__text}>
              {delegate.email ? delegate.email : '-'}
            </p>
          </div>
          <div className={classes.infoItem}>
            <p className={classes.infoItem__label}>Phone number</p>
            <p className={classes.infoItem__text}>
              {delegate.telephone ? delegate.telephone : '-'}
            </p>
          </div>
          <div className={classes.infoItem}>
            <p className={classes.infoItem__label}>Job title</p>
            <p className={classes.infoItem__text}>
              {delegate.jobTitle ? delegate.jobTitle : '-'}
            </p>
          </div>
        </div>
      </div>
      <div className={classes.personalInfo}>
        <p className={classes.subtitle}>
          Background check{' '}
          {delegate.pendingBackgroundCheck && (
            <span className={classes.pending}>(pending)</span>
          )}
        </p>
        {!delegate.hasActiveBackgroundCheck &&
          !delegate.backgroundCheckPending && (
            <>
              <p className={classes.noBackgroundCheck}>
                No TTC background check present
              </p>
              <div className={classes.buttons}>
                <Button archetype="flat" onClick={onUploadExisting}>
                  <FontAwesomeIcon icon={faClipboardCheck} color="#0957ff" />{' '}
                  Upload existing check
                </Button>
                <Button archetype="flatPrimary" onClick={onRequest}>
                  <FontAwesomeIcon icon={duoShield} /> Request background check
                </Button>
              </div>
            </>
          )}
        {delegate.pendingBackgroundCheck &&
          !delegate.pendingBackgroundCheck.isRenewal && (
            <div className={classes.backgroundCheckPending}>
              <CheckStatus
                status={getStatus(delegate.pendingBackgroundCheck)}
              />
              <p className={classes.noBackgroundCheck}>
                {statusText[getStatus(delegate.pendingBackgroundCheck)]}
              </p>
            </div>
          )}
        {delegate.currentBackgroundCheck && (
          <>
            <div className={classes.bgItems}>
              <DataItem
                label={
                  delegate.currentBackgroundCheck.expiresAt
                    ? moment(
                        delegate.currentBackgroundCheck.expiresAt
                      ).fromNow()
                    : 'ID or DBS not valid'
                }
                icon={
                  delegate.currentBackgroundCheck.expiresAt &&
                  moment(delegate.currentBackgroundCheck.expiresAt).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? faShieldCheck
                    : faShieldExclamation
                }
                iconColor={
                  delegate.currentBackgroundCheck.expiresAt &&
                  moment(delegate.currentBackgroundCheck.expiresAt).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? '#1FD426'
                    : '#f68f1f'
                }
                helpText={
                  delegate.currentBackgroundCheck.expiresAt
                    ? 'Background check expiration date'
                    : 'ID or DBS date is not valid'
                }
              >
                {delegate.currentBackgroundCheck.expiresAt
                  ? `Expires ${moment(
                      delegate.currentBackgroundCheck.expiresAt
                    ).format('DD/MM/YYYY')}`
                  : 'Background check invalid'}
              </DataItem>
              <DataItem
                label={
                  delegate.currentBackgroundCheck.idExpiryDate
                    ? `Expires ${moment(
                        delegate.currentBackgroundCheck.idExpiryDate
                      ).format('DD/MM/YYYY')}`
                    : 'Not present'
                }
                icon={
                  delegate.currentBackgroundCheck.idExpiryDate
                    ? faUserCheck
                    : faShieldExclamation
                }
                iconColor={
                  delegate.currentBackgroundCheck.idExpiryDate &&
                  moment(delegate.currentBackgroundCheck.idExpiryDate).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? '#6A728F'
                    : '#f68f1f'
                }
                helpText="ID expiration date"
                style={{ width: 'calc(50% - 5px)' }}
              >
                ID
              </DataItem>
              <DataItem
                label={
                  delegate.currentBackgroundCheck.dbsExpiryDate
                    ? `Expires ${moment(
                        delegate.currentBackgroundCheck.dbsExpiryDate
                      ).format('DD/MM/YYYY')}`
                    : 'Not present'
                }
                icon={
                  delegate.currentBackgroundCheck.dbsExpiryDate
                    ? faIdBadge
                    : faShieldExclamation
                }
                iconColor={
                  delegate.currentBackgroundCheck.dbsExpiryDate &&
                  moment(delegate.currentBackgroundCheck.dbsExpiryDate).diff(
                    moment(),
                    'days'
                  ) > 60
                    ? '#6A728F'
                    : '#f68f1f'
                }
                helpText="DBS expiration date"
                style={{ width: 'calc(50% - 5px)' }}
              >
                DBS
              </DataItem>
            </div>
            <Spacer spacing={1} vertical />
            {!delegate.pendingBackgroundCheck && (
              <Button archetype="flat" onClick={onRequest}>
                <FontAwesomeIcon icon={faRotateRight} /> Request renewal
              </Button>
            )}
          </>
        )}
        {delegate.pendingBackgroundCheck &&
          delegate.pendingBackgroundCheck.isRenewal && (
            <DataItem
              label="Background check renewal requested"
              icon={faSpinner}
              iconColor="#0b57ff"
              helpText="A renewal has been requested, and is being carried out"
            >
              Renewal pending
            </DataItem>
          )}
      </div>
    </Card>
  );
}

const ProfileCardFrag = createFragmentContainer(ProfileCard, {
  delegate: graphql`
    fragment ProfileCard_delegate on Delegate {
      uuid
      firstName
      profileImageUrl
      lastName
      email
      TTC_ID
      telephone
      jobTitle
      backgroundCheckPending
      hasActiveBackgroundCheck
      pendingBackgroundCheck {
        approvalDate
        applicationSentAt
        applicationReceivedAt
        hasAllResponses
        requestedDBS
        isRenewal
        isVerification
      }
      currentBackgroundCheck {
        approvalDate
        expiresAt
        idExpiryDate
        dbsExpiryDate
        isVerification
      }
    }
  `,
  manager: graphql`
    fragment ProfileCard_manager on Manager {
      firstName
      lastName
      company {
        backgroundCheckPennyPrice
        dbsPennyPrice
        renewalPennyPrice
        name
        verificationSuccessPennyPrice
        verificationFailurePennyPrice
        invoicing {
          dbsUsed
          dbsPrePurchased
          backgroundChecksUsed
          backgroundChecksPrePurchased
          renewalsUsed
          renewalsPrePurchased
        }
      }
    }
  `
});

export default ProfileCardFrag;
